import React, { useState, useMemo } from 'react';
import { ArrowUpDown, Copy, Check, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { CampaignPost } from '../types/calendar';

interface TableViewProps {
  posts: CampaignPost[];
  onSelectPost: (post: CampaignPost) => void;
}

type SortField = 'postId' | 'date' | 'platform' | 'phase' | 'format' | 'contentPillar' | 'status';
type SortOrder = 'asc' | 'desc';

export const TableView: React.FC<TableViewProps> = ({ posts, onSelectPost }) => {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedPosts = useMemo(() => {
    return [...posts].sort((a, b) => {
      let valA: any = a[sortField] || '';
      let valB: any = b[sortField] || '';

      if (sortField === 'date') {
        valA = a.rawDate ? a.rawDate.getTime() : 0;
        valB = b.rawDate ? b.rawDate.getTime() : 0;
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [posts, sortField, sortOrder]);

  const totalPages = Math.ceil(sortedPosts.length / pageSize) || 1;
  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedPosts.slice(start, start + pageSize);
  }, [sortedPosts, currentPage, pageSize]);

  const handleCopyCaption = (e: React.MouseEvent, post: CampaignPost) => {
    e.stopPropagation();
    if (!post.caption) return;
    navigator.clipboard.writeText(post.caption);
    setCopiedId(post.postId);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const getPlatformClass = (platform: string) => {
    const p = platform.toLowerCase();
    if (p.includes('instagram')) return 'platform-instagram';
    if (p.includes('facebook')) return 'platform-facebook';
    if (p.includes('tiktok')) return 'platform-tiktok';
    if (p.includes('pinterest')) return 'platform-pinterest';
    if (p.includes('youtube')) return 'platform-youtube';
    return '';
  };

  return (
    <div style={{
      padding: '1.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      flex: 1,
    }}>
      {/* Table Top Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.75rem',
      }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          Showing <strong>{sortedPosts.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}</strong> - <strong>{Math.min(currentPage * pageSize, sortedPosts.length)}</strong> of <strong>{sortedPosts.length}</strong> entries
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{
              padding: '0.25rem 0.5rem',
              borderRadius: '6px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-surface)',
              color: 'var(--text-main)',
              fontSize: '0.8rem',
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Main Data Table */}
      <div style={{
        background: 'var(--bg-surface)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        overflowX: 'auto',
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          textAlign: 'left',
          fontSize: '0.85rem',
        }}>
          <thead>
            <tr style={{
              background: 'var(--bg-primary)',
              borderBottom: '1px solid var(--border-color)',
            }}>
              {[
                { field: 'postId', label: 'Post ID' },
                { field: 'date', label: 'Date' },
                { field: 'platform', label: 'Platform' },
                { field: 'phase', label: 'Phase' },
                { field: 'format', label: 'Format' },
                { field: 'contentPillar', label: 'Content Pillar' },
                { field: 'status', label: 'Status' },
              ].map(col => (
                <th
                  key={col.field}
                  onClick={() => handleSort(col.field as SortField)}
                  style={{
                    padding: '0.75rem 1rem',
                    fontWeight: 700,
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    {col.label}
                    <ArrowUpDown size={13} style={{ color: sortField === col.field ? 'var(--accent-primary)' : 'var(--text-light)' }} />
                  </div>
                </th>
              ))}
              <th style={{ padding: '0.75rem 1rem', fontWeight: 700, color: 'var(--text-muted)' }}>Hook & Content Preview</th>
              <th style={{ padding: '0.75rem 1rem', fontWeight: 700, color: 'var(--text-muted)', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPosts.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  No matching posts found.
                </td>
              </tr>
            ) : (
              paginatedPosts.map(post => (
                <tr
                  key={post.postId}
                  onClick={() => onSelectPost(post)}
                  style={{
                    borderBottom: '1px solid var(--border-color)',
                    cursor: 'pointer',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 700, color: 'var(--accent-primary)', whiteSpace: 'nowrap' }}>
                    {post.postId}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                    {post.date} <span style={{ color: 'var(--text-light)', fontSize: '0.75rem' }}>({post.day})</span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                    <span className={`platform-badge ${getPlatformClass(post.platform)}`}>
                      {post.platform}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                    <span className="badge" style={{ background: 'var(--bg-primary)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
                      {post.phase}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                    {post.format}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {post.contentPillar}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                    <span className="badge" style={{ background: 'var(--bg-primary)', color: 'var(--text-main)', border: '1px solid var(--border-color)' }}>
                      {post.status}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', maxWidth: '300px' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {post.hook || post.contentToCreate || 'No Hook'}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.4rem' }}>
                      {post.caption && (
                        <button
                          onClick={(e) => handleCopyCaption(e, post)}
                          style={{
                            padding: '0.3rem 0.5rem',
                            borderRadius: '6px',
                            border: '1px solid var(--border-color)',
                            background: 'var(--bg-primary)',
                            color: copiedId === post.postId ? '#10b981' : 'var(--text-muted)',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.2rem',
                            fontSize: '0.75rem',
                          }}
                          title="Copy Caption"
                        >
                          {copiedId === post.postId ? <Check size={13} /> : <Copy size={13} />}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectPost(post);
                        }}
                        style={{
                          padding: '0.3rem 0.55rem',
                          borderRadius: '6px',
                          border: '1px solid var(--accent-primary)',
                          background: 'var(--accent-glow)',
                          color: 'var(--accent-primary)',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.2rem',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        <Eye size={13} /> View
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          marginTop: '0.5rem',
        }}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
            style={{
              padding: '0.4rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-surface)',
              color: 'var(--text-main)',
              opacity: currentPage === 1 ? 0.5 : 1,
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.85rem',
            }}
          >
            <ChevronLeft size={16} /> Prev
          </button>

          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
            style={{
              padding: '0.4rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-surface)',
              color: 'var(--text-main)',
              opacity: currentPage === totalPages ? 0.5 : 1,
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.85rem',
            }}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};
